import { useState, useCallback, useRef, useEffect } from 'react';
import { type GameState, type Message, type Piece, type JoinStartedGameOptionsPayload, type LobbyStatePayload, type BotsMatchOfferPayload } from '@sheshdash/shared';
import { useWebSocket } from './useWebSocket';
import {
  getWebSocketUrl,
  refreshAccessTokenIfNeeded,
  getForceDiceQueue,
  loadLastJoin,
  saveLastJoin,
  getPieceAtPosition as getPieceAtPositionHelper,
} from '../utils/gameStateHelpers';
import { unlockAudio } from '../utils/audioManager';
const gameStateDebug = import.meta.env.DEV;

// Auto-rejoin backoff schedule for the "player name already taken" race
// (the server hasn't marked the previous WS as disconnected yet — typical
// after a fast page reload, especially during a backend deploy when pods
// are spinning up). Total budget ≈ 32s — long enough to ride out a deploy,
// short enough that a player who genuinely can't reconnect (game expired,
// banned, etc.) doesn't sit on a frozen UI forever.
const AUTO_REJOIN_BACKOFF_MS = [300, 800, 1600, 3000, 5000, 8000, 13000];

type E2EState = {
  currentGameId: string | null;
  joinStatus: 'idle' | 'pending' | 'joined' | 'failed';
  lastEventType: string | null;
  lastError: string | null;
  playerId: string | null;
  phase: GameState['phase'] | null;
  waitingGameIds: string[];
};

function setWindowE2EState(patch: Partial<E2EState>) {
  if (typeof window === 'undefined') {
    return;
  }
  const current = (window as unknown as { __e2e?: E2EState }).__e2e ?? {
    currentGameId: null,
    joinStatus: 'idle',
    lastEventType: null,
    lastError: null,
    playerId: null,
    phase: null,
    waitingGameIds: [],
  };
  (window as unknown as { __e2e: E2EState }).__e2e = {
    ...current,
    ...patch,
  };
}

function getTestGameSource() {
  if (typeof window === 'undefined') {
    return '';
  }
  const storageValue = window.localStorage.getItem('sheshdash_e2e_game_source')?.trim() ?? '';
  if (storageValue) {
    return storageValue;
  }
  const searchValue = new URLSearchParams(window.location.search).get('test_source')?.trim() ?? '';
  return searchValue;
}

function filterPrivateCurrentGameFromLobby(
  lobby: LobbyStatePayload,
  currentGame: GameState | null,
): LobbyStatePayload {
  if (!currentGame?.id || !currentGame.is_private) {
    return lobby;
  }

  return {
    ...lobby,
    waiting_games: lobby.waiting_games.filter((game) => game.id !== currentGame.id),
  };
}

export interface UseGameStateOptions {
  /** ADR-002 §1: lets feature hooks (e.g. usePreviewMove) observe every
   *  incoming WS message without owning their own socket. Called for
   *  every message after the leave-game gate but before the switch
   *  dispatch, so observers see PREVIEW_MOVE_RESULT / request-tagged
   *  ERROR / etc. The observer MUST NOT throw; errors are not caught. */
  onMessage?: (message: Message) => void;
}

export function useGameState(options: UseGameStateOptions = {}) {
  const onMessageRef = useRef(options.onMessage);
  useEffect(() => {
    onMessageRef.current = options.onMessage;
  }, [options.onMessage]);

  const forceDiceQueueRef = useRef<number[]>(getForceDiceQueue());
  const sendMessageRef = useRef<(type: Message['type'], payload: Message['payload']) => void>(() => undefined);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [joinStartedGameOptions, setJoinStartedGameOptions] = useState<JoinStartedGameOptionsPayload | null>(null);
  const [pendingStartedGameJoin, setPendingStartedGameJoin] = useState<{ gameId: string; playerName: string } | null>(null);
  const [lobbyState, setLobbyState] = useState<LobbyStatePayload | null>(null);
  const [botsMatchOffer, setBotsMatchOffer] = useState<BotsMatchOfferPayload | null>(null);
  const [diceRollEventId, setDiceRollEventId] = useState(0);
  const lastJoinRef = useRef<{ gameId: string; playerName: string; reconnectToken?: string } | null>(loadLastJoin());
  const pendingStartedGameJoinRef = useRef<{ gameId: string; playerName: string } | null>(null);

  // Используем ref для отслеживания playerId в callback без пересоздания
  const playerIdRef = useRef<string | null>(null);

  // Флаг для предотвращения обновления состояния после выхода из игры
  const leavingGameRef = useRef<boolean>(false);
  const pendingQuickStartRef = useRef<boolean>(false);
  const pendingAutoStartRef = useRef<boolean>(false);
  // Счётчик ретраев при гонке авторекконнекта (IsDisconnected ещё не выставлен на сервере)
  const reconnectRetryRef = useRef<number>(0);

  const handleMessage = useCallback((message: Message) => {
    if (gameStateDebug) {
      console.log('Received message:', message);
    }

    // Игнорируем сообщения после выхода из игры
    if (leavingGameRef.current) {
      if (gameStateDebug) {
        console.log('🚪 Ignoring message - leaving game');
      }
      return;
    }

    // ADR-002 §1: external observers (e.g. usePreviewMove) get a copy of
    // every message before the local switch runs. Read-only — observers
    // are not allowed to mutate the message envelope.
    onMessageRef.current?.(message);

    switch (message.type) {
      case 'GAME_STATE':
        if (gameStateDebug) {
          console.log('🎮 Game State Update:', {
            currentPlayer: message.payload.current_player,
            lastDiceRoll: message.payload.last_dice_roll,
            canPlacePiece: message.payload.can_place_piece,
            canRollAgain: message.payload.can_roll_again,
            phase: message.payload.phase,
            isPaused: message.payload.is_paused,
            pausedBy: message.payload.paused_by ?? null,
            turnDeadline: message.payload.turn_deadline ?? null,
          });
        }
        setGameState(message.payload);
        pendingStartedGameJoinRef.current = null;
        setPendingStartedGameJoin(null);
        setError(null);
        setWindowE2EState({
          currentGameId: message.payload.id,
          joinStatus: 'joined',
          lastEventType: message.type,
          lastError: null,
          phase: message.payload.phase,
        });
        break;

      case 'GAME_CREATED':
        playerIdRef.current = message.payload.player_id;
        setPlayerId(message.payload.player_id);
        setError(null);
        setBotsMatchOffer(null);
        {
          const reconnectToken = message.payload.reconnect_token;
          lastJoinRef.current = {
            gameId: message.payload.game_id,
            playerName: lastJoinRef.current?.playerName || '',
            reconnectToken: reconnectToken || lastJoinRef.current?.reconnectToken,
          };
          saveLastJoin(lastJoinRef.current);
        }
        setWindowE2EState({
          currentGameId: message.payload.game_id,
          joinStatus: 'joined',
          lastEventType: message.type,
          lastError: null,
          playerId: message.payload.player_id,
        });
        if (pendingQuickStartRef.current) {
          pendingQuickStartRef.current = false;
          pendingAutoStartRef.current = true;
          sendMessageRef.current('ADD_BOT', { count: 3 });
        }
        break;

      case 'PLAYER_JOINED': {
        reconnectRetryRef.current = 0;
        const approvedStartedGameJoin = pendingStartedGameJoinRef.current;
        pendingStartedGameJoinRef.current = null;
        setPendingStartedGameJoin(null);
        // If message contains player_id, it's for us (direct message from server)
        if (message.payload.player_id && message.payload.player_id !== playerIdRef.current) {
          if (gameStateDebug) {
            console.log('✅ Setting my player ID:', message.payload.player_id);
          }
          playerIdRef.current = message.payload.player_id;
          setPlayerId(message.payload.player_id);
        }
        if (approvedStartedGameJoin) {
          // REQUEST_JOIN was approved: only now does this game become the
          // auto-rejoin target (a rejected/failed request must not be).
          lastJoinRef.current = {
            gameId: approvedStartedGameJoin.gameId,
            playerName: message.payload.player_name || approvedStartedGameJoin.playerName,
            reconnectToken: message.payload.reconnect_token || undefined,
          };
          saveLastJoin(lastJoinRef.current);
        } else if (lastJoinRef.current) {
          const reconnectToken = message.payload.reconnect_token;
          if (reconnectToken) {
            lastJoinRef.current.reconnectToken = reconnectToken;
            saveLastJoin(lastJoinRef.current);
          }
        }
        setError(null);
        setWindowE2EState({
          joinStatus: 'joined',
          lastEventType: message.type,
          lastError: null,
          playerId: message.payload.player_id || playerIdRef.current,
        });
        break;
      }

      case 'BOT_ADDED':
        setError(null);
        setWindowE2EState({
          joinStatus: 'joined',
          lastEventType: message.type,
          lastError: null,
        });
        if (pendingAutoStartRef.current) {
          pendingAutoStartRef.current = false;
          sendMessageRef.current('START_GAME', {});
        }
        break;

      case 'GAME_STARTED':
        setError(null);
        setBotsMatchOffer(null);
        break;

      case 'DICE_ROLLED':
        setError(null);
        setDiceRollEventId((prev) => prev + 1);
        break;

      case 'MOVE_RESULT':
        setError(null);
        break;

      case 'GAME_OVER':
        setError(null);
        break;

      case 'ERROR':
        // ADR-002 §1: ERROR with `request_id` is a per-request reply (e.g.
        // PREVIEW_MOVE rejected). The observer above already routed it to
        // the originating feature hook (usePreviewMove); we do NOT raise
        // it as a global banner here.
        if (typeof message.payload?.request_id === 'string' && message.payload.request_id) {
          break;
        }
        if (typeof message.payload?.message === 'string') {
          const normalizedMessage = message.payload.message.toLowerCase();
          const hasStoredGameTarget = Boolean(lastJoinRef.current?.gameId);
          const isStaleJoinTarget =
            normalizedMessage.includes('game not found') ||
            normalizedMessage.includes('game has finished') ||
            normalizedMessage.includes('game is finished') ||
            normalizedMessage.includes('not in a game');
          if (isStaleJoinTarget) {
            // Stale reconnect target (deleted/expired game): silently clear auto-join state.
            reconnectRetryRef.current = 0;
            pendingStartedGameJoinRef.current = null;
            setPendingStartedGameJoin(null);
            lastJoinRef.current = null;
            saveLastJoin(null);
            // Refresh lobby state to show current available games
            sendMessageRef.current('GET_LOBBY_STATE', {});
            break;
          }
          // Race condition: page reloaded so fast that the server hasn't marked the player
          // as IsDisconnected yet, so the first reconnect attempt can fail with
          // "player name is already taken".
          // Retry on a backoff schedule so we ride through a deploy or load spike instead
          // of giving up after ~1 s and dropping the user into the lobby mid-game.
          // A pending REQUEST_JOIN is not a reconnect: its name error is final.
          if (
            hasStoredGameTarget &&
            !pendingStartedGameJoinRef.current &&
            normalizedMessage.includes('player name is already taken')
          ) {
            if (reconnectRetryRef.current < AUTO_REJOIN_BACKOFF_MS.length) {
              const delay = AUTO_REJOIN_BACKOFF_MS[reconnectRetryRef.current];
              reconnectRetryRef.current += 1;
              const lj = lastJoinRef.current;
              setTimeout(() => {
                if (!lj?.gameId) return;
                sendMessageRef.current('JOIN_GAME', {
                  game_id: lj.gameId,
                  player_name: lj.playerName || '',
                  ...(lj.reconnectToken ? { reconnect_token: lj.reconnectToken } : {}),
                });
              }, delay);
              break;
            }
            // Gave up after the full backoff schedule — game is genuinely stale, go to lobby
            reconnectRetryRef.current = 0;
            pendingStartedGameJoinRef.current = null;
            setPendingStartedGameJoin(null);
            lastJoinRef.current = null;
            saveLastJoin(null);
            sendMessageRef.current('GET_LOBBY_STATE', {});
            break;
          }
        }
        pendingStartedGameJoinRef.current = null;
        setPendingStartedGameJoin(null);
        if (typeof message.payload?.existing_game_id === 'string' && message.payload.existing_game_id) {
          const existingGameID = message.payload.existing_game_id;
          if (lastJoinRef.current?.playerName) {
            lastJoinRef.current = {
              gameId: existingGameID,
              playerName: lastJoinRef.current.playerName,
              reconnectToken: lastJoinRef.current.reconnectToken,
            };
            saveLastJoin(lastJoinRef.current);
            // Redirect by protocol: join the user's already active game.
            sendMessageRef.current('JOIN_GAME', {
              game_id: existingGameID,
              player_name: lastJoinRef.current.playerName,
              reconnect_token: lastJoinRef.current.reconnectToken,
            });
          }
          setError(message.payload.message);
          break;
        }
        setError(message.payload.message);
        setWindowE2EState({
          joinStatus: 'failed',
          lastEventType: message.type,
          lastError: message.payload.message ?? 'unknown error',
        });
        break;

      case 'JOIN_STARTED_GAME_OPTIONS':
        if (gameStateDebug) {
          console.log('📋 Received JOIN_STARTED_GAME_OPTIONS:', message.payload);
        }
        setJoinStartedGameOptions(message.payload as JoinStartedGameOptionsPayload);
        setError(null);
        break;

      case 'JOIN_REQUEST_RESULT':
        if (gameStateDebug) {
          console.log('📋 Join request result:', message.payload);
        }
        // Clear join options dialog if shown
        setJoinStartedGameOptions(null);
        setError(null);
        if (message.payload?.approved === false) {
          pendingStartedGameJoinRef.current = null;
          setPendingStartedGameJoin(null);
        } else if (message.payload?.approved === true && pendingStartedGameJoinRef.current) {
          // The approving vote registers this socket into the game before the
          // final GAME_STATE broadcast. Ask for a direct refresh as a guard
          // against message ordering/backpressure so the requester doesn't
          // remain on the lobby screen after approval.
          sendMessageRef.current('GET_GAME_STATE', {});
        }
        break;

      case 'KICK_REQUEST_RESULT':
        setError(null);
        break;

      case 'LOBBY_STATE':
        if (gameStateDebug) {
          console.log('🏢 Lobby State Update:', message.payload);
        }
        {
          const incoming = message.payload as LobbyStatePayload;
          const current = (window as unknown as { __lastGameState?: GameState | null } | undefined)?.__lastGameState ?? gameState ?? null;
          const filteredLobby = filterPrivateCurrentGameFromLobby(incoming, current);
          setLobbyState(filteredLobby);
          setWindowE2EState({
            lastEventType: message.type,
            waitingGameIds: Array.isArray(filteredLobby.waiting_games)
              ? filteredLobby.waiting_games.map((game: { id: string }) => game.id)
              : [],
          });
        }
        break;
      case 'BOTS_MATCH_OFFER': {
        const offer = message.payload as BotsMatchOfferPayload;
        if (!offer?.expires_at) {
          setBotsMatchOffer(null);
          break;
        }
        setBotsMatchOffer(offer);
        break;
      }

      default:
        if (gameStateDebug) {
          console.log('Unknown message type:', message.type);
        }
    }
  }, []);

  const { sendMessage, isConnected: wsConnected } = useWebSocket(getWebSocketUrl, {
    onMessage: handleMessage,
    onBeforeConnect: refreshAccessTokenIfNeeded,
    onOpen: () => {
      if (leavingGameRef.current) {
        return;
      }
      const lastJoin = lastJoinRef.current;
      if (lastJoin?.gameId) {
        sendMessage('JOIN_GAME', {
          game_id: lastJoin.gameId,
          player_name: lastJoin.playerName || '',
          ...(lastJoin.reconnectToken ? { reconnect_token: lastJoin.reconnectToken } : {}),
        });
      } else {
        sendMessage('GET_LOBBY_STATE', {});
      }
    },
  });

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    (window as unknown as { __lastGameState?: GameState | null }).__lastGameState = gameState;
  }, [gameState]);

  useEffect(() => {
    setWindowE2EState({
      currentGameId: gameState?.id ?? lastJoinRef.current?.gameId ?? null,
      playerId,
      phase: gameState?.phase ?? null,
      lastError: error,
    });
  }, [gameState, playerId, error]);

  useEffect(() => {
    if (!botsMatchOffer?.expires_at) {
      return;
    }
    const expiresAt = new Date(botsMatchOffer.expires_at).getTime();
    if (Number.isNaN(expiresAt)) {
      return;
    }
    const delay = expiresAt - Date.now();
    if (delay <= 0) {
      setBotsMatchOffer(null);
      return;
    }
    const timer = window.setTimeout(() => {
      setBotsMatchOffer(null);
    }, delay + 150);
    return () => window.clearTimeout(timer);
  }, [botsMatchOffer]);

  const getLobbyState = useCallback(() => {
    sendMessage('GET_LOBBY_STATE', {});
  }, [sendMessage]);

  const refreshGameState = useCallback(() => {
    if (!lastJoinRef.current?.gameId) {
      return;
    }
    sendMessage('GET_GAME_STATE', {});
  }, [sendMessage]);

  const joinQueue = useCallback((playerName: string) => {
    lastJoinRef.current = { gameId: '', playerName, reconnectToken: undefined };
    saveLastJoin(lastJoinRef.current);
    setWindowE2EState({
      currentGameId: null,
      joinStatus: 'idle',
      lastEventType: 'JOIN_QUEUE',
      lastError: null,
      phase: null,
      waitingGameIds: [],
    });
    sendMessage('JOIN_QUEUE', { player_name: playerName });
  }, [sendMessage]);

  const leaveQueue = useCallback(() => {
    sendMessage('LEAVE_QUEUE', {});
    setBotsMatchOffer(null);
  }, [sendMessage]);

  const acceptBotsMatch = useCallback(() => {
    sendMessage('ACCEPT_BOTS_MATCH', {});
    setBotsMatchOffer(null);
  }, [sendMessage]);

  const declineBotsMatch = useCallback(() => {
    sendMessage('DECLINE_BOTS_MATCH', {});
    setBotsMatchOffer(null);
  }, [sendMessage]);

  const createGame = useCallback((playerName: string, isPrivate = false, preferredColor = '') => {
    const testSource = getTestGameSource();
    lastJoinRef.current = { gameId: '', playerName, reconnectToken: undefined };
    saveLastJoin(lastJoinRef.current);
    setWindowE2EState({
      currentGameId: null,
      joinStatus: 'pending',
      lastEventType: 'CREATE_GAME',
      lastError: null,
      phase: null,
    });
    sendMessage('CREATE_GAME', {
      player_name: playerName,
      is_private: isPrivate,
      ...(preferredColor ? { preferred_color: preferredColor } : {}),
      ...(testSource ? { source: testSource } : {}),
    });
  }, [sendMessage]);

  const quickStartGame = useCallback((playerName: string, preferredColor = '') => {
    pendingQuickStartRef.current = true;
    pendingAutoStartRef.current = false;
    const testSource = getTestGameSource();
    lastJoinRef.current = { gameId: '', playerName, reconnectToken: undefined };
    saveLastJoin(lastJoinRef.current);
    setWindowE2EState({
      currentGameId: null,
      joinStatus: 'pending',
      lastEventType: 'CREATE_GAME',
      lastError: null,
      phase: null,
    });
    // Quick Start is "single-player vs 3 bots, auto-started" — by definition
    // not something we want surfaced in `LOBBY_STATE.waiting_games` between
    // CREATE_GAME and the auto ADD_BOT × 3 + START_GAME chain that follows.
    // Send is_private=true so the room stays out of the public lobby for the
    // entire (brief) wait window.
    sendMessage('CREATE_GAME', {
      player_name: playerName,
      is_private: true,
      ...(preferredColor ? { preferred_color: preferredColor } : {}),
      ...(testSource ? { source: testSource } : {}),
    });
  }, [sendMessage]);

  const joinGame = useCallback((gameId: string, playerName: string, preferredColor = '') => {
    lastJoinRef.current = { gameId, playerName, reconnectToken: undefined };
    saveLastJoin(lastJoinRef.current);
    setWindowE2EState({
      currentGameId: gameId,
      joinStatus: 'pending',
      lastEventType: 'JOIN_GAME',
      lastError: null,
      phase: null,
    });
    sendMessage('JOIN_GAME', {
      game_id: gameId,
      player_name: playerName,
      ...(preferredColor ? { preferred_color: preferredColor } : {}),
    });
  }, [sendMessage]);

  const rollDice = useCallback(() => {
    const nextForced = forceDiceQueueRef.current.length > 0 ? forceDiceQueueRef.current.shift() : null;
    const payload = nextForced ? { force_value: nextForced } : {};
    sendMessage('ROLL_DICE', payload);
    setError(null);
  }, [sendMessage]);

  const placePiece = useCallback((pieceId: string) => {
    sendMessage('PLACE_PIECE', { piece_id: pieceId });
    setError(null);
  }, [sendMessage]);

  const movePiece = useCallback((pieceId: string) => {
    sendMessage('MOVE_PIECE', { piece_id: pieceId });
    setError(null);
  }, [sendMessage]);

  const addBots = useCallback((count: number) => {
    sendMessage('ADD_BOT', { count });
    setError(null);
  }, [sendMessage]);

  const startGame = useCallback(() => {
    if (!wsConnected) {
      setError('Connecting...');
      return;
    }
    sendMessage('START_GAME', {});
    setError(null);
  }, [sendMessage, wsConnected]);

  const voteJoinRequest = useCallback((approve: boolean) => {
    sendMessage('VOTE_JOIN_REQUEST', { approve });
    setError(null);
  }, [sendMessage]);

  const requestJoin = useCallback((gameId: string, playerName: string, replaceBot: boolean, botId: string, keepPieces: boolean) => {
    // lastJoin is saved on PLAYER_JOINED once the request is approved; saving
    // it here would make a rejected request an auto-rejoin target.
    const pendingJoin = { gameId, playerName };
    pendingStartedGameJoinRef.current = pendingJoin;
    setPendingStartedGameJoin(pendingJoin);
    setWindowE2EState({
      currentGameId: gameId,
      joinStatus: 'pending',
      lastEventType: 'REQUEST_JOIN',
      lastError: null,
      phase: null,
    });
    sendMessage('REQUEST_JOIN', {
      game_id: gameId,
      player_name: playerName,
      replace_bot: replaceBot,
      bot_id: botId,
      keep_pieces: keepPieces
    });
    setError(null);
  }, [sendMessage]);

  const requestKick = useCallback((targetPlayerId: string) => {
    sendMessage('REQUEST_KICK', {
      target_player_id: targetPlayerId,
    });
    setError(null);
  }, [sendMessage]);

  const voteKickRequest = useCallback((approve: boolean) => {
    sendMessage('VOTE_KICK_REQUEST', { approve });
    setError(null);
  }, [sendMessage]);

  const setGamePrivacy = useCallback((isPrivate: boolean) => {
    sendMessage('SET_GAME_PRIVACY', { is_private: isPrivate });
    setError(null);
  }, [sendMessage]);

  const togglePause = useCallback((paused: boolean) => {
    // When resuming, kick the AudioContext awake within this user-gesture
    // tick. Safari/iOS suspends the context after a few seconds of pause-
    // induced inactivity, and the audio-active useEffect that fires when
    // is_paused flips back to false runs too late (post-render) for the
    // gesture deadline.
    if (!paused) {
      try { unlockAudio(); } catch { /* ignore */ }
    }
    if (gameStateDebug) {
      console.log('[pause] toggle requested', {
        paused,
        gameId: gameState?.id ?? null,
        currentPlayer: gameState?.current_player ?? null,
        currentPlayerIsBot: gameState ? Boolean(gameState.players[gameState.current_player]?.is_bot) : false,
        isPaused: gameState?.is_paused ?? null,
        wsConnected,
      });
    }
    sendMessage('TOGGLE_PAUSE', { paused });
    setError(null);
  }, [gameState, sendMessage, wsConnected]);

  // Выход из игры - сброс всего состояния
  const leaveGame = useCallback(() => {
    if (gameStateDebug) {
      console.log('🚪 Leaving game...');
    }
    // Устанавливаем флаг выхода, чтобы игнорировать входящие сообщения
    leavingGameRef.current = true;
    lastJoinRef.current = null;
    saveLastJoin(null);
    // Отправляем сообщение серверу о выходе (если сервер поддерживает)
    sendMessage('LEAVE_GAME', {});
    // Сбрасываем локальное состояние
    playerIdRef.current = null;
    setPlayerId(null);
    setGameState(null);
    setError(null);
    setJoinStartedGameOptions(null);
    setWindowE2EState({
      currentGameId: null,
      joinStatus: 'idle',
      lastEventType: 'LEAVE_GAME',
      lastError: null,
      playerId: null,
      phase: null,
    });
    // Сбрасываем флаг через 1 секунду, чтобы можно было снова присоединиться к игре
    setTimeout(() => {
      leavingGameRef.current = false;
      // Resubscribe to lobby updates
      sendMessage('GET_LOBBY_STATE', {});
    }, 1000);
    setBotsMatchOffer(null);
  }, [sendMessage]);

  // Очистить диалог присоединения к запущенной игре
  const clearJoinStartedGameOptions = useCallback(() => {
    setJoinStartedGameOptions(null);
  }, []);

  const getCurrentPlayer = useCallback(() => {
    if (!gameState || gameState.players.length === 0) return null;
    return gameState.players[gameState.current_player];
  }, [gameState]);

  const isMyTurn = useCallback(() => {
    if (!gameState || !playerId) return false;
    const currentPlayer = getCurrentPlayer();
    return currentPlayer?.id === playerId;
  }, [gameState, playerId, getCurrentPlayer]);

  const getMyPlayer = useCallback(() => {
    if (!gameState || !playerId) return null;
    return gameState.players.find(p => p.id === playerId) || null;
  }, [gameState, playerId]);

  const getPieceAtPosition = useCallback((row: number, col: number): Piece | null => {
    return getPieceAtPositionHelper(gameState, playerId, row, col);
  }, [gameState, playerId]);

  return {
    gameState,
    playerId,
    error,
    diceRollEventId,
    isConnected: wsConnected,
    // ADR-002 §1: feature hooks (e.g. usePreviewMove) drive the WS via
    // this same sender — no second socket. Caller passes back the hook's
    // `consumeMessage` through the optional `onMessage` option.
    sendMessage,
    joinStartedGameOptions,
    pendingStartedGameJoin,
    botsMatchOffer,
    lobbyState,
    createGame,
    quickStartGame,
    joinGame,
    startGame,
    rollDice,
    placePiece,
    movePiece,
    addBots,
    voteJoinRequest,
    requestJoin,
    requestKick,
    voteKickRequest,
    setGamePrivacy,
    togglePause,
    leaveGame,
    getLobbyState,
    refreshGameState,
    joinQueue,
    leaveQueue,
    acceptBotsMatch,
    declineBotsMatch,
    clearJoinStartedGameOptions,
    getCurrentPlayer,
    isMyTurn,
    getMyPlayer,
    getPieceAtPosition,
  };
}
